import { useCallback, useEffect, useState } from 'react';

import { useToast } from 'hooks/useToast';
import { apiOrchestrator } from 'services/apiService';

interface SocialNetwork {
  id?: number;
  company_uuid: string;
  social_network_type: string;
  social_network_username: string;
}

// Evento personalizado para notificar atualizações
const SOCIAL_NETWORKS_UPDATED = 'socialNetworksUpdated';

export const useCompanySocialNetworks = (companyUuid: string) => {
  const { toastSuccess, toastError } = useToast();
  const [networkTypes, setNetworkTypes] = useState<string[]>([]);
  const [socialNetworks, setSocialNetworks] = useState<SocialNetwork[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSocialNetworks = useCallback(async () => {
    if (!companyUuid) return;

    setLoading(true);
    try {
      const response = await apiOrchestrator.get(`/company-social-networks/${companyUuid}`);
      setSocialNetworks(response.data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar redes sociais');
      toastError('Erro ao carregar redes sociais');
    } finally {
      setLoading(false);
    }
  }, [companyUuid, toastError]);

  // Função para notificar atualizações
  const notifyUpdate = useCallback(() => {
    const event = new CustomEvent(SOCIAL_NETWORKS_UPDATED, {
      detail: { companyUuid },
    });
    window.dispatchEvent(event);
  }, [companyUuid]);

  useEffect(() => {
    const fetchNetworkTypes = async () => {
      try {
        const response = await apiOrchestrator.get('/user-social-networks/types');
        setNetworkTypes(response.data);
      } catch (err) {
        setError('Erro ao carregar tipos de redes sociais');
        toastError('Erro ao carregar tipos de redes sociais');
      }
    };

    fetchNetworkTypes();
    fetchSocialNetworks();
  }, [companyUuid, toastError, fetchSocialNetworks]);

  const upsertSocialNetwork = async (socialNetwork: Omit<SocialNetwork, 'company_uuid'>) => {
    if (!companyUuid) return;

    setLoading(true);
    try {
      const existingNetwork = socialNetworks.find(
        network => network.social_network_type === socialNetwork.social_network_type
      );

      let response: { data: SocialNetwork };
      if (existingNetwork?.id) {
        // Se existe, atualiza
        response = await apiOrchestrator.put<SocialNetwork>(
          `/company-social-networks/${existingNetwork.id}`,
          {
            ...socialNetwork,
            company_uuid: companyUuid,
          }
        );
      } else {
        // Se não existe, cria
        response = await apiOrchestrator.post<SocialNetwork>(`/company-social-networks/`, {
          ...socialNetwork,
          company_uuid: companyUuid,
        });
      }

      // Atualiza a lista de redes sociais
      await fetchSocialNetworks();

      // Notifica que houve uma atualização
      notifyUpdate();

      toastSuccess(
        `Rede social ${socialNetwork.social_network_type} ${existingNetwork ? 'atualizada' : 'adicionada'} com sucesso!`
      );
      setError(null);
    } catch (err) {
      setError('Erro ao atualizar rede social');
      toastError('Erro ao atualizar rede social');
    } finally {
      setLoading(false);
    }
  };

  const deleteSocialNetwork = async (networkId: number) => {
    setLoading(true);
    try {
      await apiOrchestrator.delete(`/company-social-networks/${networkId}`);
      await fetchSocialNetworks();

      // Notifica que houve uma atualização
      notifyUpdate();

      toastSuccess('Rede social removida com sucesso');
      setError(null);
    } catch (err) {
      setError('Erro ao remover rede social');
      toastError('Erro ao remover rede social');
    } finally {
      setLoading(false);
    }
  };

  const getUsernameByType = (type: string): SocialNetwork | undefined => {
    return socialNetworks.find(net => net.social_network_type === type);
  };

  // Hook para escutar atualizações
  const useSocialNetworksUpdate = (callback: () => void) => {
    useEffect(() => {
      const handleUpdate = (event: CustomEvent) => {
        if (event.detail.companyUuid === companyUuid) {
          callback();
        }
      };

      window.addEventListener(SOCIAL_NETWORKS_UPDATED, handleUpdate as EventListener);
      return () => {
        window.removeEventListener(SOCIAL_NETWORKS_UPDATED, handleUpdate as EventListener);
      };
    }, [companyUuid, callback]);
  };

  return {
    networkTypes,
    socialNetworks,
    loading,
    error,
    upsertSocialNetwork,
    deleteSocialNetwork,
    getUsernameByType,
    useSocialNetworksUpdate,
    refreshSocialNetworks: fetchSocialNetworks,
  };
};
